// src/pages/Switches.logica.js — las reglas de la matriz de VLANs
//
// Vive aparte de la pantalla porque son reglas de red, no de dibujo, y
// porque el backend tiene que aplicar EXACTAMENTE las mismas antes de
// mandarle nada al switch: lo que valide el navegador no cuenta, cualquiera
// puede pegarle a la API por su cuenta. Aquí están juntas y en un solo
// lugar para que las dos copias no se separen con el tiempo.
//
// La regla que manda, de la que sale todo lo demás:
//
//     UN PUERTO SOLO PUEDE ESTAR UNTAGGED (U) EN UNA VLAN.
//
// Esa VLAN es su PVID: la que se le asigna al tráfico que llega sin
// etiqueta. Por eso las T no compiten entre sí (la etiqueta las
// distingue) pero dos U en el mismo puerto son una contradicción.
//
// Todo se indexa por el NOMBRE DE LA INTERFAZ (`GE1/0/3`), nunca por el
// número de puerto: en el S620 la numeración se reinicia en cada grupo y
// hay un "puerto 1" en GE, en 10GE, en 25GE y en 100GE.

export const UNTAGGED = "U";
export const TAGGED = "T";
export const EXCLUIDO = "E";

/* ------------------------------------------------------------------ */
/* Distribución física de los puertos                                 */
/* ------------------------------------------------------------------ */

/** Cómo se le dice a un puerto en pantalla: "GE 3", "10GE 12". */
export function etiquetaPuerto(p) {
  return `${p.grupo} ${p.n}`;
}

/** Cómo se rotula cada grupo. Lo que no esté aquí se rotula con su clave,
 *  que es mejor que no rotular nada cuando llegue un equipo con puertos
 *  que hoy no existen. */
export const ETIQUETA_GRUPO = {
  GE: "1G cobre",
  "10GE": "10G SFP+",
  "25GE": "25G SFP28",
  "40GE": "40G QSFP+",
  "100GE": "100G QSFP28",
};

/**
 * Los grupos salen de los puertos que reportó el equipo, en el orden en
 * que vienen — no de una lista fija en el código. Así el día que se
 * registre un switch distinto, su chasis se dibuja solo.
 */
export function gruposDePuertos(puertos = []) {
  const vistos = [];
  puertos.forEach((p) => {
    if (!vistos.some((g) => g.clave === p.grupo)) {
      vistos.push({ clave: p.grupo, etiqueta: ETIQUETA_GRUPO[p.grupo] || p.grupo });
    }
  });
  return vistos;
}

/**
 * Acomoda los puertos como están en el chasis: un bloque por grupo, y
 * dentro de cada bloque los impares arriba y los pares abajo, como los
 * conectores reales.
 *
 * Sale de la lista que reporte el equipo, no de un número fijo: el mismo
 * componente sirve para el S620 de 50 puertos y para el que llegue
 * después.
 */
export function distribuirPorGrupos(puertos = [], grupos = []) {
  return grupos
    .map((g) => {
      const delGrupo = puertos.filter((p) => p.grupo === g.clave);
      return {
        clave: g.clave,
        etiqueta: g.etiqueta,
        arriba: delGrupo.filter((p) => p.n % 2 === 1),
        abajo: delGrupo.filter((p) => p.n % 2 === 0),
      };
    })
    .filter((g) => g.arriba.length || g.abajo.length);
}

/* ------------------------------------------------------------------ */
/* Lectura de la matriz                                               */
/* ------------------------------------------------------------------ */

export function celda(vlan, puertoId) {
  return vlan?.puertos?.[puertoId] || EXCLUIDO;
}

/** La VLAN que tiene la U de este puerto, o null si no tiene ninguna. */
export function vlanUntaggedDe(vlans, puertoId) {
  return vlans.find((v) => celda(v, puertoId) === UNTAGGED) || null;
}

/**
 * Qué es este puerto, leyendo su COLUMNA completa de arriba abajo. El
 * carácter del puerto no lo define una celda suelta: una T sola no lo
 * vuelve trunk, es la combinación la que lo dice.
 */
export function caracterDelPuerto(vlans, puertoId) {
  const untagged = vlanUntaggedDe(vlans, puertoId);
  const tagged = vlans.filter((v) => celda(v, puertoId) === TAGGED);

  if (!untagged && tagged.length === 0) return { tipo: "libre", untagged, tagged };
  if (untagged && tagged.length === 0) return { tipo: "acceso", untagged, tagged };
  if (!untagged) return { tipo: "trunk-puro", untagged, tagged };
  return { tipo: "trunk", untagged, tagged };
}

export const ETIQUETA_CARACTER = {
  libre: "Sin VLANs",
  acceso: "Acceso",
  trunk: "Trunk",
  "trunk-puro": "Trunk puro",
};

/** Un puerto que todavía negocia su tipo de enlace (el `desirable` de
 *  fábrica) no está configurado: deja pasar todas las VLANs y el switch
 *  decide solo. Se marca aparte para no confundirlo con uno vacío. */
export function negociaSolo(puerto) {
  return puerto.linkType === "desirable";
}

/* ------------------------------------------------------------------ */
/* Lo que no se deja hacer, y lo que sí se deja pero no se guarda      */
/* ------------------------------------------------------------------ */

/**
 * Bloqueo duro: lo único que la app impide de plano. Regresa null o
 * `{ motivo, marcar }`.
 *
 * Solo entra aquí lo que NO tiene arreglo dentro de la matriz: quitarle
 * la VLAN de gestión al puerto por el que se administra el switch. No es
 * una configuración inválida —el equipo la aceptaría feliz— sino la
 * garantía de perder el acceso y tener que ir en persona.
 *
 * En el S620 esto casi nunca se dispara: la gestión entra por MEth0/0/0,
 * fuera de banda, que ni siquiera está en la matriz. La regla se queda
 * para el día que alguien administre en banda por un Vlanif.
 */
export function bloqueoDuro({ puerto, vlanId, valor, vlanGestion }) {
  if (puerto.gestion && vlanId === vlanGestion && valor === EXCLUIDO) {
    return {
      motivo: `${puerto.interfaz} es por donde se administra el switch. ` +
        `Si le quitas la VLAN ${vlanId} el equipo queda incomunicado y hay que ir a conectarse en sitio.`,
      marcar: [{ vlanId, puerto: puerto.id }],
    };
  }
  return null;
}

/**
 * Conflictos de la matriz: puertos con más de una U.
 *
 * Estos SÍ se dejan poner. La celda se queda donde quien opera la puso
 * —en U, que es lo que pidió— y el conflicto se queda pintado en rojo
 * hasta que se resuelva. Lo que se impide es **guardar**: mandar esto al
 * switch dejaría un puerto con dos VLANs sin etiqueta.
 *
 * Se hace así y no bloqueando el clic porque el arreglo está en OTRA
 * celda (la de la VLAN que ya tenía la U). Dejar la intención puesta y
 * señalar las dos es lo que hace evidente qué hay que ir a cambiar; si
 * la celda se revierte sola, se pierde lo que se quería hacer y encima
 * queda un estado que sí se puede guardar.
 */
export function conflictosDeUntagged(vlans, puertos) {
  return puertos
    .map((p) => {
      const conU = vlans.filter((v) => celda(v, p.id) === UNTAGGED);
      if (conU.length < 2) return null;

      const lista = conU.map((v) => `${v.id} (${v.nombre})`).join(" y ");
      return {
        puerto: p,
        vlans: conU,
        motivo: `${p.interfaz}${p.nombre ? ` (${p.nombre})` : ""} tiene ${conU.length} VLANs sin etiqueta: ${lista}. ` +
          `Un puerto solo puede tener una —esa es su VLAN nativa—: deja una en U y las otras en T o en E.`,
        marcar: conU.map((v) => ({ vlanId: v.id, puerto: p.id })),
      };
    })
    .filter(Boolean);
}

/* ------------------------------------------------------------------ */
/* Avisos (no bloquean, solo advierten)                               */
/* ------------------------------------------------------------------ */

/**
 * Cosas que están permitidas pero casi siempre son un olvido. Se muestran
 * al pie de la matriz, sin impedir guardar. Solo se miran los puertos con
 * enlace: en un switch de 50 puertos, avisar de los apagados sería ruido.
 */
export function avisosDeLaMatriz(vlans, puertos) {
  const avisos = [];

  // Lo primero, porque afecta a todo lo demás: mientras un puerto negocie
  // su tipo de enlace, deja pasar las 4094 VLANs y el switch puede
  // cambiarle el modo por su cuenta.
  const enAuto = puertos.filter(negociaSolo);
  if (enAuto.length) {
    avisos.push(
      `${enAuto.length} de ${puertos.length} puertos siguen en negociación automática ` +
      `(\`desirable\`): hoy dejan pasar todas las VLANs y el switch decide solo si son ` +
      `acceso o trunk. Al configurarlos desde aquí pasan a hybrid con lista explícita.`
    );
  }

  puertos
    .filter((p) => p.estado === "up")
    .forEach((p) => {
      const { tipo, tagged } = caracterDelPuerto(vlans, p.id);
      const como = `${p.interfaz}${p.nombre ? ` (${p.nombre})` : ""}`;

      if (tipo === "trunk-puro") {
        avisos.push(
          `${como}: solo tiene VLANs etiquetadas (${tagged.map((v) => v.id).join(", ")}). ` +
          `Lo que llegue sin etiqueta se descarta.`
        );
      }
      if (tipo === "libre") {
        avisos.push(`${como}: está enlazado pero no pasa ninguna VLAN.`);
      }
    });

  return avisos;
}

/* ------------------------------------------------------------------ */
/* Traducción a comandos Huawei                                       */
/* ------------------------------------------------------------------ */

/**
 * El nombre de la VLAN viaja al equipo como `description`, no como `name`.
 *
 * Son dos campos distintos en Huawei y `display vlan` —el que lee la app—
 * muestra la DESCRIPTION. Escribiendo `name` el nombre nunca regresaría de
 * vuelta. Con description, además, se pueden usar espacios: "SW ANCONA" se
 * guarda tal cual y no hay que mutilarlo a SW_ANCONA.
 *
 * Solo se limpia lo que le haría daño a la CLI: comillas, interrogaciones
 * (que en VRP abren la ayuda en línea) y saltos de línea.
 */
export function normalizarNombreVlan(nombre) {
  return (nombre || "")
    .replace(/\s+/g, " ")
    .replace(/["'?]/g, "")
    .trim()
    .slice(0, 80);
}

/**
 * Compara la matriz guardada contra la editada y arma los comandos.
 *
 * Solo el DIFF: nunca se reescribe la configuración completa, porque
 * reescribir toca puertos que nadie pidió tocar.
 *
 * Ojo con `undo port hybrid vlan 1`: en Huawei TODOS los puertos nacen
 * untagged en la VLAN 1. Si se pone una U nueva sin quitar esa, el puerto
 * queda con dos untagged. Es exactamente el "debe ser E en default" que
 * EdgeSwitch obliga a mano; aquí sale del diff solo, porque la regla de la
 * única U ya dejó la 1 en E.
 *
 * Provisional: esto lo va a generar el backend y la pantalla solo mostrará
 * lo que le regrese, para que no haya dos generadores que se desfasen.
 */
export function generarComandos({ original, actual, puertos, puertosOriginales = [] }) {
  const bloques = [];
  const antesPorId = Object.fromEntries(puertosOriginales.map((p) => [p.id, p]));

  const idsAntes = original.map((v) => v.id);
  const idsAhora = actual.map((v) => v.id);

  // 1. VLANs nuevas: primero existir, luego usarse.
  const nuevas = actual.filter((v) => !idsAntes.includes(v.id));
  if (nuevas.length) {
    bloques.push({
      titulo: "VLANs nuevas",
      comandos: nuevas.flatMap((v) => [
        `vlan ${v.id}`,
        ` description ${normalizarNombreVlan(v.nombre)}`,
        " quit",
      ]),
    });
  }

  // 2. Nombres que cambiaron.
  const renombradas = actual.filter((v) => {
    const antes = original.find((o) => o.id === v.id);
    return antes && normalizarNombreVlan(antes.nombre) !== normalizarNombreVlan(v.nombre);
  });
  if (renombradas.length) {
    bloques.push({
      titulo: "Nombres",
      comandos: renombradas.flatMap((v) => [
        `vlan ${v.id}`,
        ` description ${normalizarNombreVlan(v.nombre)}`,
        " quit",
      ]),
    });
  }

  // 3. Puertos cuya columna cambió.
  puertos.forEach((p) => {
    const comandos = [];
    const antesU = vlanUntaggedDe(original, p.id);
    const ahoraU = vlanUntaggedDe(actual, p.id);

    // Lo que sale primero, para no pasar por un estado con dos untagged.
    actual.concat(original.filter((o) => !idsAhora.includes(o.id))).forEach((v) => {
      const antes = celda(original.find((o) => o.id === v.id), p.id);
      const ahora = idsAhora.includes(v.id) ? celda(actual.find((a) => a.id === v.id), p.id) : EXCLUIDO;
      if (antes !== EXCLUIDO && ahora === EXCLUIDO) {
        comandos.push(` undo port hybrid vlan ${v.id}`);
      }
    });

    actual.forEach((v) => {
      const antes = celda(original.find((o) => o.id === v.id), p.id);
      const ahora = celda(v, p.id);
      if (antes === ahora) return;

      if (ahora === TAGGED) {
        if (antes === UNTAGGED) comandos.push(` undo port hybrid vlan ${v.id}`);
        comandos.push(` port hybrid tagged vlan ${v.id}`);
      }
      if (ahora === UNTAGGED) {
        if (antes === TAGGED) comandos.push(` undo port hybrid vlan ${v.id}`);
        comandos.push(` port hybrid untagged vlan ${v.id}`);
      }
    });

    // El PVID va después de las untagged: es a cuál de ellas pertenece el
    // tráfico que entra sin etiqueta.
    if (ahoraU && ahoraU.id !== antesU?.id) {
      comandos.push(` port hybrid pvid vlan ${ahoraU.id}`);
    }

    // La descripción del puerto va en el mismo bloque que su columna de
    // VLANs: es el mismo `interface X`, y verlo junto es más claro que
    // repetir el puerto en dos sitios del diff.
    const nombreAntes = (antesPorId[p.id]?.nombre || "").trim();
    const nombreAhora = (p.nombre || "").trim();
    const cambioNombre = nombreAhora !== nombreAntes
      ? [nombreAhora ? ` description ${nombreAhora}` : " undo description"]
      : [];

    if (comandos.length || cambioNombre.length) {
      bloques.push({
        titulo: `${p.interfaz}${nombreAntes ? ` — ${nombreAntes}` : ""}`,
        comandos: [
          `interface ${p.interfaz}`,
          // Un puerto que venía como access o trunk hay que pasarlo a
          // hybrid antes de nada; si ya es hybrid, el comando sobra pero
          // no molesta.
          ...cambioNombre,
          comandos.length && p.linkType !== "hybrid" ? " port link-type hybrid" : null,
          ...comandos,
          " quit",
        ].filter(Boolean),
      });
    }
  });

  // 4. VLANs borradas, hasta el final: mientras algún puerto la use, el
  //    switch se queja.
  const borradas = original.filter((v) => !idsAhora.includes(v.id));
  if (borradas.length) {
    bloques.push({
      titulo: "VLANs eliminadas",
      comandos: borradas.map((v) => `undo vlan ${v.id}`),
    });
  }

  // 5. Sin `save` el cambio se pierde en el próximo reinicio del equipo.
  //    Este S620 aplica de inmediato (el prompt no trae ~ ni *), así que
  //    NO lleva `commit`.
  if (bloques.length) {
    bloques.push({ titulo: "Guardar en el equipo", comandos: ["save"] });
  }

  return bloques;
}

export function hayCambios(original, actual, puertos) {
  return generarComandos({ original, actual, puertos }).length > 0;
}


/* ------------------------------------------------------------------ */
/* Qué puertos toca un cambio                                          */
/* ------------------------------------------------------------------ */

/**
 * Los puertos que aparecen en el diff. Sirve para enseñar en el chasis
 * DÓNDE cae el cambio: una lista de comandos no dice si estás tocando el
 * puerto del router o uno libre, y el mapa sí.
 */
export function puertosDelPlan(bloques, puertos) {
  const porInterfaz = new Set(puertos.map((p) => p.interfaz));
  return bloques
    .map((b) => b.titulo.split(" — ")[0].trim())
    .filter((t) => porInterfaz.has(t));
}
